package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.RenderCheckReportDto
import dev.lingvo.shared.RenderDto
import dev.lingvo.shared.ShotCheckSeverity
import dev.lingvo.shared.ShotPushPreflightDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.StoreShotsDto
import dev.lingvo.site.i18n.tr

/** Selection is at set granularity because store APIs replace one locale/device set together. */
data class ReviewSetKey(val locale: String, val device: DeviceClass)

/** Hash comparisons are meaningful only when the remote store supplied hashes. */
data class ReviewSetRow(
    val key: ReviewSetKey, val renders: List<RenderDto>, val ready: Boolean,
    val newCount: Int, val sameCount: Int, val removedCount: Int,
)

/** Uses the authoritative render check; store preview failure never means an empty remote set. */
fun reviewRows(renders: List<RenderDto>, checks: RenderCheckReportDto, store: StoreShotsDto?): List<ReviewSetRow> =
    (renders.groupBy { ReviewSetKey(it.locale, it.deviceClass) } + checks.missing.map { ReviewSetKey(it.locale, it.deviceClass) }.distinct()
        .filter { key -> renders.none { it.locale == key.locale && it.deviceClass == key.device } }.associateWith { emptyList() }).map { (key, images) ->
        val remote = store?.sets?.firstOrNull { it.locale == key.locale && it.deviceClass == key.device && it.readable }
        val same = images.count { image -> remote?.images?.any { it.sha256 == image.sha256 } == true }
        ReviewSetRow(key, images, images.isNotEmpty() && images.all { image -> checks.renders.any { it.renderId == image.id && it.pushable } } &&
            checks.missing.none { it.locale == key.locale && it.deviceClass == key.device },
            images.size - same, same, remote?.images?.count { before -> images.none { it.sha256 == before.sha256 } } ?: 0)
    }.sortedWith(compareBy<ReviewSetRow> { it.key.locale }.thenBy { it.key.device.id })

/** Composed sets can be reviewed before they pass approval checks; missing sets cannot be selected. */
fun defaultScreenshotPushSelection(rows: List<ReviewSetRow>): Set<ReviewSetKey> = rows.filter { it.renders.isNotEmpty() }.map { it.key }.toSet()

/** A recheck can remove eligibility but can never add an unchecked row to a pending push. */
fun screenshotPushableSelection(rows: List<ReviewSetRow>, selected: Set<ReviewSetKey>): Set<ReviewSetKey> =
    rows.filter { it.ready && it.key in selected }.map { it.key }.toSet()

/** S2 carries two lists, so a sparse matrix cannot represent the reviewer's consent. */
fun selectionIsRectangle(selected: Set<ReviewSetKey>): Boolean = selected.isNotEmpty() &&
    selected.size == selected.map { it.locale }.toSet().size * selected.map { it.device }.toSet().size

/** Each disabled state explains the next action; red check text belongs to the server verbatim. */
fun screenshotPushBlockedReason(
    selected: Boolean, approvalExists: Boolean, approvalValid: Boolean, rectangle: Boolean,
    preflight: ShotPushPreflightDto?, running: Boolean = false,
): String? = when {
    running -> tr("app.projects.slug.screenshots.review_model.wait_for_the_current_push_to", "Wait for the current push to finish.")
    !selected -> tr("app.projects.slug.screenshots.review_model.select_at_least_one_screenshot_set", "Select at least one screenshot set.")
    !approvalExists -> tr("app.projects.slug.screenshots.review_model.approve_this_set_before_pushing", "Approve this set before pushing.")
    !approvalValid -> tr("app.projects.slug.screenshots.review_model.this_approval_changed_approve_the_current", "This approval changed. Approve the current set again.")
    !rectangle -> tr("app.projects.slug.screenshots.review_model.select_the_same_devices_in_each", "Select the same devices in each selected language.")
    preflight == null -> tr("app.projects.slug.screenshots.review_model.checking_this_set", "Checking this set…")
    preflight.checks.any { it.severity == ShotCheckSeverity.RED } -> preflight.checks.first { it.severity == ShotCheckSeverity.RED }.message
    !preflight.editable -> preflight.stateNote ?: tr("app.projects.slug.screenshots.review_model.the_app_store_has_no_editable", "The App Store has no editable version right now. Create one in App Store Connect, then push again.")
    !preflight.pushable -> preflight.stateNote ?: tr("app.projects.slug.screenshots.review_model.resolve_the_checks_before_pushing", "Resolve the checks before pushing.")
    else -> null
}

/** Consent describes recovery during failure separately from user rollback after success. */
fun screenshotPushNotice(platform: StorePlatform): String = when (platform) {
    StorePlatform.GOOGLE_PLAY -> tr("app.projects.slug.screenshots.review_model.google_play_keeps_no_copy_of", "Google Play keeps no copy of the set you replace, so a push can't be rolled back. Lingvo changes nothing until every image is uploaded.")
    StorePlatform.APP_STORE -> tr("app.projects.slug.screenshots.review_model.lingvo_uploads_every_new_image_before", "Lingvo uploads every new image before changing the set and puts the old set back if the push fails midway. After a push succeeds, Lingvo can't roll it back.")
}


/** Request epochs reject late successes and failures even when a refreshed selection is structurally equal. */
class ReviewRequestEpoch {
    private var generation = 0L
    /** Invalidates every previously dispatched read before a new read or pending transition. */
    fun next(): Long { generation++; return generation }
    /** A read may publish only while no newer validation has begun. */
    fun isCurrent(request: Long): Boolean = request == generation
}

/** New compositions start ticked; refreshing never ticks a set the person already unticked. */
fun refreshScreenshotSelection(rows: List<ReviewSetRow>, selected: Set<ReviewSetKey>, known: Set<ReviewSetKey>): Set<ReviewSetKey> {
    val composed = defaultScreenshotPushSelection(rows)
    return (selected + (composed - known)).intersect(composed)
}
